import React, { useEffect, useState } from "react";
import MatchXp from "../../../shared/progress-ui/MatchXp";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare, isFlagCode } from "../../../shared/flags";
import { modeLook, ordinal, SIZE_LABEL } from "../../../online/dynamic";
import type { DynamicLayerProps, PublicPlayer } from "./types";
import NextRoundReveal, { SET_FLIP_MS, ModeIcon, revealHasResult, standingsFor, PASS_LEAD_MS, REVEAL_MS, revealLeadMs, revealMs, revealStraddles, revealVariant, useStraddlingReveal } from "./NextRoundReveal";
import s from "./PlayNowLayer.module.scss";

// Play Now's screens over the game (sketch D72): the calm "you play next
// round" card for someone who drops in, the map-size toasts and the next
// round's reveal between rounds, the set podium when a set ends, and the rare
// hand-over to a fuller room. The scoreboard, the top bar
// and the leave button are the game's own. With the banner design the pause has
// one band: the standings with the round's winner marked, then the next round
// in a second row under it (a map size change rides there instead of a toast).

// The last part of every pause belongs to the next round's reveal. Two designs
// stay on into the next round (the pass-through, which only needs the pause's
// last moment, and the banner): those are run by useStraddlingReveal instead.
const PASS = revealVariant() === "pass";
const BANNER = revealHasResult(revealVariant()); // the banner, or a board: the pause's one screen
const STRADDLES = revealStraddles(revealVariant());
const REVEAL_LEN = revealMs(revealVariant());
const REVEAL_WINDOW = revealLeadMs(revealVariant());
// What the pause shows before the reveal keeps its time whatever the reveal's
// length: a longer one (the banner) begins earlier and sits above it meanwhile.
const SCREENS_WINDOW = PASS ? PASS_LEAD_MS : REVEAL_MS + 200;
// The set podium fades away over the pause's last moment, as the next arena comes in.
const PODIUM_FADE_MS = 850;

const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const hexPts = (cx: number, cy: number, r: number) =>
  Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 180) * (60 * k - 90);
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
const colorOf = (p: PublicPlayer | undefined) => (p && (wormColors as Record<string, string>)[p.colorId]) || "#8ea3bd";

const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const Check = () => (<svg {...svgProps} strokeWidth={2.6}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>);

// A player's round flag, ringed in their colour (a plain colour disc without one).
function Face({ player, className }: { player: PublicPlayer | undefined; className?: string }) {
  const c = colorOf(player);
  return player && isFlagCode(player.flag)
    ? <img className={className} src={flagUrlSquare(player.flag as string)} alt="" style={{ boxShadow: `0 0 0 2px ${c}` }} />
    : <i className={className} style={{ background: c, boxShadow: `0 0 0 2px ${c}` }} />;
}

// The map outline growing (or shrinking) inside a toast.
function MapSize({ grow, rect }: { grow: boolean; rect: boolean }) {
  const rx = rect ? 3 : 17;
  return (
    <svg className={s.mapSvg} viewBox="0 0 64 40" aria-hidden="true">
      <rect className={grow ? s.mapGhostSmall : s.mapGhostBig} x="3" y="3" width="58" height="34" rx={rx} />
      <rect className={grow ? s.mapGrow : s.mapShrink} x="3" y="3" width="58" height="34" rx={rx} />
    </svg>
  );
}

// Milliseconds into the pause, ticking while it lasts (0 outside it). Derived
// from pauseStartedAt, so a remount mid-pause lands in the right place.
function usePauseClock(active: boolean, startedAt: number) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => tick((n) => n + 1), 100);
    return () => window.clearInterval(id);
  }, [active, startedAt]);
  return active ? Math.max(0, Date.now() - startedAt) : 0;
}

export default function PlayNowLayer(props: DynamicLayerProps) {
  const { phase, setup, next, result, players, playerById, seatIds, youIds, pending, pauseStartedAt, pauseMs, moved, getGameState, onLeave, roundWinnerId, roundWinnerPoints, onResults, hasSetResults } = props;

  const paused = phase === "countdown" && !moved;
  const elapsed = usePauseClock(paused, pauseStartedAt);
  const remaining = paused ? Math.max(0, pauseMs - elapsed) : 0;
  const setOver = paused && !!(result && result.setOver);

  // ---------- the reveal: the pause's last seconds ----------
  // Its length is fixed when it first shows (it plays once per pause).
  const revealDue = !STRADDLES && paused && !!next && !!next.mode && remaining <= REVEAL_WINDOW && remaining > 0;
  const [reveal, setReveal] = useState<{ at: number; ms: number } | null>(null);
  useEffect(() => {
    if (!paused) { if (reveal) setReveal(null); return; }
    if (revealDue && (!reveal || reveal.at !== pauseStartedAt)) {
      const ms = Math.min(REVEAL_LEN, pauseMs - (Date.now() - pauseStartedAt) - 120);
      setReveal({ at: pauseStartedAt, ms: ms >= 1000 ? ms : 0 }); // too late for it: skip
    }
  }, [paused, revealDue, pauseStartedAt, pauseMs, reveal]);
  const showReveal = paused && !!reveal && reveal.at === pauseStartedAt && reveal.ms > 0 && !!next;
  // What the pause showed until then stays until the wipe has covered it. The
  // banner covers nothing: the set card is gone a moment before it comes in.
  // This one moment (ms into the pause) is when the card leaves, and what its
  // clock and its button's bar count to.
  const screensEnd = Math.max(1, pauseMs - (BANNER ? REVEAL_WINDOW + 400 : SCREENS_WINDOW - 450));
  const covered = paused && elapsed >= screensEnd;

  // ---------- dropping in: watching the round in progress ----------
  const watching = phase === "playing" && !moved && (pending || (seatIds.length > 0 && youIds.length === 0));
  const nextLook = setup && setup.nextMode ? modeLook(setup.nextMode) : null;

  // "Keep playing" changes nothing (the room carries on by itself): it only answers.
  const [kept, setKept] = useState(false);
  useEffect(() => { if (!setOver) setKept(false); }, [setOver]);

  const who = (id: string) => playerById[id] || players.find((p) => p.id === id);

  const revealKicker = setOver
    ? `Next round · 1/${(next && next.setRounds) || (setup && setup.setRounds) || 7}`
    : next && next.setRound && next.setRounds
      ? `Next round · ${next.setRound}/${next.setRounds}`
      : "Next round";
  const sizeChange = paused && !setOver && next && (next.sizeChange === "bigger" || next.sizeChange === "smaller") ? next.sizeChange : null;
  // The banner's first row: the standings after the round, its winner marked
  // (not after a set: the podium said it). Everyone when 7 or fewer play; with
  // more, the top 5 and my best-placed seat with its real rank.
  const standings = BANNER && paused && players.length > 0
    ? standingsFor(players, props.score || {}, seatIds, roundWinnerId, roundWinnerPoints, setup && setup.setRound ? `Round ${setup.setRound}` : undefined, props.scoreBefore)
    : undefined;
  // The round's winner, for the line at the top.
  const winP = paused && roundWinnerId ? who(roundWinnerId) : undefined;
  const roundWin = winP ? { name: winP.name, color: colorOf(winP), flagUrl: isFlagCode(winP.flag) ? flagUrlSquare(winP.flag as string) : null } : null;
  // After a set: who won it (the top of the set's standings), shown under the
  // round's standings before the card turns over into the podium.
  const setTop = setOver ? (result!.standings || []).slice().sort((a, b) => b.score - a.score)[0] : undefined;
  const setTopP = setTop ? who(setTop.id) : undefined;
  const setWin = !setOver ? undefined : setTopP ? { name: setTopP.name, color: colorOf(setTopP), flagUrl: isFlagCode(setTopP.flag) ? flagUrlSquare(setTopP.flag as string) : null } : null;
  const straddlingReveal = useStraddlingReveal(paused, pauseStartedAt, pauseMs, next && next.mode ? {
    mode: next.mode, context: "playnow", kicker: revealKicker,
    line: BANNER && sizeChange ? (sizeChange === "bigger" ? "Bigger map" : "Smaller map") : undefined,
    standings,
    winner: roundWin,
    setEnd: setWin !== undefined ? { winner: setWin } : null,
  } : null, props.getGameState);

  // ---------- d: the room is handing me on ----------
  if (moved) {
    return (
      <div className={s.root}>
        <div className={`${s.scrim} ${s.scrimDeep}`}>
          <div className={s.take} role="status">
            <div className={s.takeHex}>
              <svg viewBox="0 0 84 84" aria-hidden="true">
                <polygon points={hexPts(42, 42, 39)} fill="rgba(25,240,176,0.07)" stroke="rgba(255,255,255,0.1)" strokeWidth="3" strokeLinejoin="round" />
                <polygon className={s.ringSpin} points={hexPts(42, 42, 39)} fill="none" stroke="#19f0b0" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" pathLength={100} strokeDasharray="22 78" />
              </svg>
            </div>
            <h3>Joining a fuller room</h3>
            <span className={s.takeBar}><i /></span>
          </div>
        </div>
      </div>
    );
  }

  // ---------- c: the set podium ----------
  let setCard: React.ReactNode = null;
  // (the podium waits for the paced card to turn over: SET_FLIP_MS into the pause)
  // It stays to the end of the pause and fades as the next round comes in.
  const podiumEnd = BANNER ? pauseMs : screensEnd;
  if (setOver && elapsed < podiumEnd && (!BANNER || elapsed >= SET_FLIP_MS + 380)) {
    const standings = (result!.standings || []).slice().sort((a, b) => b.score - a.score);
    const top = standings.slice(0, 3);
    const order = top.length >= 3 ? [1, 0, 2] : top.length === 2 ? [1, 0] : [0]; // 2nd, 1st, 3rd
    const mine = seatIds
      .map((id) => ({ id, i: standings.findIndex((r) => r.id === id) }))
      .filter((m) => m.i >= 0)
      .slice(0, 2);
    const secs = Math.max(0, Math.round((podiumEnd - elapsed) / 1000)); // reads 0:00 as the card leaves
    const rounds = (setup && setup.setRounds) || (next && next.setRounds) || 7;
    setCard = (
      <div className={`${s.scrim} ${BANNER && elapsed >= podiumEnd - PODIUM_FADE_MS ? s.scrimLeaving : ""}`}>
        <div className={s.setCard} data-xp-host="">
          <div className={s.setLeft}>
            <span className={s.kicker}>Match over · {rounds} rounds</span>
            <div className={s.podium} style={{ gridTemplateColumns: order.map((i) => (i === 0 ? "1.1fr" : "1fr")).join(" ") }}>
              {order.map((i) => {
                const row = top[i];
                const p = who(row.id);
                const place = i + 1;
                return (
                  <div key={row.id} className={`${s.pod} ${place === 1 ? s.podWin : ""} ${s[`pod${place}`]}`} style={{ "--pc": colorOf(p) } as React.CSSProperties}>
                    <Face player={p} className={s.face} />
                    <b>{p ? p.name : "Player"}</b>
                    <small>{row.score} pts</small>
                    <span className={s.block}>
                      {place === 1 && <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points={hexPts(12, 12, 10)} /></svg>}
                      <em>{place}</em>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={s.setRight}>
            {mine.map((m) => (
              <div key={m.id} className={s.mine}>
                <Face player={who(m.id)} className={s.face} />
                <span>
                  <small>{mine.length > 1 ? who(m.id)?.name || "Your match" : "Your match"}</small>
                  <b>{ordinal(m.i + 1)} of {standings.length} · {standings[m.i].score} pts</b>
                </span>
              </div>
            ))}
            <MatchXp />
            <div className={s.nextSet}>
              <small>Next match</small>
              <b>{mmss(secs)}</b>
            </div>
            <div className={s.setBtns}>
              <button type="button" className={s.ghost} onClick={onLeave}>Leave</button>
              {/* the set's full results (stats included), over the game; the next match starts behind them */}
              {onResults && hasSetResults && <button type="button" className={s.ghost} onClick={onResults}>Results</button>}
              <button type="button" className={s.primary} onClick={() => setKept(true)}>
                <i className={s.auto} style={{ width: `${Math.min(100, (elapsed / podiumEnd) * 100)}%` }} />
                <span>{kept ? <><Check />You are in</> : "Play next match"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- b: the map changes size (a light toast under the top bar) ----------
  const change = !BANNER && !covered ? sizeChange : null;
  const fromSize = setup ? SIZE_LABEL[setup.sizeKey] : "";
  const toSize = next && next.sizeKey ? SIZE_LABEL[next.sizeKey] : "";

  const nowLook = modeLook(setup && setup.mode);

  return (
    <div className={s.root}>
      {setCard}

      {change && (
        <div className={`${s.toasts} ${s.toastsPause}`}>
          <div className={`${s.toast} ${s.toastMap}`} role="status">
            <MapSize grow={change === "bigger"} rect={(next!.shape || (setup && setup.shape)) === "Rectangle"} />
            <span className={s.toastTxt}>
              <b>{change === "bigger" ? "Bigger map next round" : "Smaller map next round"}</b>
              {fromSize && toSize && fromSize !== toSize && <small>{fromSize} to {toSize}</small>}
            </span>
          </div>
        </div>
      )}

      {/* Dropped in mid-round: in the middle of the screen so it is not missed.
          What you will play, when the room says (an older server does not). */}
      {watching && (
        <div className={s.dropIn} role="status">
          <h3>You play next round</h3>
          {nextLook && (
            <p style={{ "--c": nextLook.color } as React.CSSProperties}>
              <span className={s.dropIcon}><ModeIcon mode={setup!.nextMode} /></span>
              <b>{nextLook.name}</b>
            </p>
          )}
        </div>
      )}

      {showReveal && (
        <NextRoundReveal
          key={reveal!.at}
          mode={next!.mode as string}
          shape={next!.shape || (setup && setup.shape) || "Stadium"}
          sizeKey={next!.sizeKey || (setup && setup.sizeKey) || "medium"}
          context="playnow"
          kicker={revealKicker}
          durationMs={reveal!.ms}
        />
      )}
      {straddlingReveal}
    </div>
  );
}
